import React, { useState, useEffect, useCallback } from 'react';
import './SliderControls.css';
import { useApi } from '../../../api/api';

// Base Slider Component - used internally
const SliderBase = ({ 
  title,
  value,
  dbValue,
  min,
  max,
  step,
  onChange,
  disabled,
  isUncertainty = false,
  valueFormat,
  insights = null,
  onInsightsClick = null
}) => {
  return (
    <div className='value-control'>
      <div>
        <h3><b>{title}</b></h3>
        <div className="slider-value">{typeof value === 'number' ? (valueFormat ? valueFormat(value) : value.toFixed(2)) : value}</div>
      </div>
      <div className="slider-container">
        <div className="slider-minmax">{min}</div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={`slider ${isUncertainty ? 'uncertainty-slider-input' : ''}`}
        />
        <div className="slider-minmax">{max}</div>
      </div>
      
      {dbValue !== undefined && (
        <div className="db-value-indicator">
          Baseline value: {typeof dbValue === 'number' ? (valueFormat ? valueFormat(dbValue) : dbValue.toFixed(2)) : dbValue}
        </div>
      )}
      
      {value !== dbValue && (
        <div className="value-changed-indicator"></div>
      )}
      
      {insights && (
        <div 
          className={`ai-insights ${onInsightsClick ? 'clickable' : ''}`}
          onClick={onInsightsClick ? () => onInsightsClick('Natural Mortality Rate') : undefined}
        >
          <div className="ai-insights-label">Insights from best available science</div>
          <div className="ai-insights-text">{insights}</div>
          {onInsightsClick && (
            <div className="ai-insights-click-hint">Click to explore detailed research</div>
          )}
        </div>
      )}
    </div>
  );
};

// P* Value Slider
export const PStarSlider = ({ 
  pStarValue, 
  dbPStarValue, 
  handlePStarChange, 
  updatingPStar,
  selectedStockId 
}) => {
  return (
    <SliderBase
      title={
        <span>
          ABC Control Rule (<i>%F<sub>MSY</sub></i>)
        </span>
      }
      value={pStarValue}
      dbValue={dbPStarValue}
      min={0.1}
      max={1}
      step={0.01}
      onChange={handlePStarChange}
      disabled={updatingPStar}
    />
  );
};

// P* Uncertainty Slider
export const PStarUncertaintySlider = ({ 
  pStarUncertaintyValue, 
  dbPStarUncertaintyValue, 
  handlePStarUncertaintyChange, 
  updatingPStar 
}) => {
  return (
    <SliderBase
      title="P* Uncertainty"
      value={pStarUncertaintyValue}
      dbValue={dbPStarUncertaintyValue}
      min={0.01}
      max={0.2}
      step={0.01}
      onChange={handlePStarUncertaintyChange}
      disabled={updatingPStar}
      isUncertainty={true}
    />
  );
};

// Management Adjustment Slider
export const ManagementAdjSlider = ({ 
  managementAdjValue, 
  dbManagementAdjValue, 
  handleManagementAdjChange, 
  updatingPStar,
  selectedStockId 
}) => {
  return (
    <SliderBase
      title={`Management Confidence`}
      value={managementAdjValue}
      dbValue={dbManagementAdjValue}
      min={0.9}
      max={1.0}
      step={0.01}
      onChange={handleManagementAdjChange}
      disabled={updatingPStar}
    />
  );
};

// Management Adjustment Uncertainty Slider
export const ManagementAdjUncertaintySlider = ({ 
  managementAdjUncertaintyValue, 
  dbManagementAdjUncertaintyValue, 
  handleManagementAdjUncertaintyChange, 
  updatingPStar 
}) => {
  return (
    <SliderBase
      title="Management Adjustment Uncertainty"
      value={managementAdjUncertaintyValue}
      dbValue={dbManagementAdjUncertaintyValue}
      min={0.01}
      max={0.2}
      step={0.01}
      onChange={handleManagementAdjUncertaintyChange}
      disabled={updatingPStar}
      isUncertainty={true}
    />
  );
};

// Rebuilding Time Slider
export const RebuildingTimeSlider = ({ 
  rebuildingTimeValue, 
  dbRebuildingTimeValue, 
  handleRebuildingTimeChange, 
  updatingPStar,
  selectedStockId 
}) => {
  return (
    <SliderBase
      title={
        <span>
          Timeline Rebuild (years)
        </span>
      }
      value={rebuildingTimeValue}
      dbValue={dbRebuildingTimeValue}
      min={5}
      max={20}
      step={1}
      onChange={handleRebuildingTimeChange}
      disabled={updatingPStar}
      valueFormat={v => v.toFixed(0)}
    />
  );
};

// Natural Mortality Slider
export const NaturalMortalitySlider = ({ 
  naturalMortalityValue, 
  dbNaturalMortalityValue,
  handleNaturalMortalityChange, 
  updatingPStar,
  selectedStockId,
  onInsightsClick = null
}) => {
  const { getBasParameterTracking } = useApi();
  const [insights, setInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);

  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };

  // Function to calculate insights based on historical M values
  const calculateInsights = useCallback(async (currentValue) => {
    if (!currentValue || updatingPStar) return;

    setIsLoadingInsights(true);
    try {
      // Fetch historical M values from BAS API
      const response = await getBasParameterTracking('Natural Mortality Rate', selectedStockId);
      const data = response.data;
      
      if (!data || !data.results) {
        setInsights(null);
        return;
      }

      // Extract all M values from the last 10 years
      const currentYear = new Date().getFullYear();
      const tenYearsAgo = currentYear - 10;
      
      const historicalValues = [];
      
      data.results.forEach(paper => {
        // Check if paper is from the last 10 years
        if (paper.publication_date) {
          const paperYear = new Date(paper.publication_date).getFullYear();
          if (paperYear >= tenYearsAgo) {
            paper.extraction.results.forEach(result => {
              const value = parseFloat(result.value);
              if (!isNaN(value)) {
                historicalValues.push(value);
              }
            });
          }
        }
      });

      if (historicalValues.length === 0) {
        setInsights(null);
        return;
      }

      // Calculate percentile
      const sortedValues = historicalValues.sort((a, b) => a - b);
      const percentile = (sortedValues.filter(v => v < currentValue).length / sortedValues.length) * 100;
      
        // Generate insight text
        let insightText;
        if (percentile >= 90) {
          insightText = (
            <span>
              {currentValue.toFixed(3)} is higher than {percentile.toFixed(0)}% of <i>M</i> values over the last 10 years
            </span>
          );
        } else if (percentile <= 10) {
          insightText = (
            <span>
              {currentValue.toFixed(3)} is lower than {(100 - percentile).toFixed(0)}% of <i>M</i> values over the last 10 years
            </span>
          );
        } else {
          const ordinalPercentile = getOrdinalSuffix(Math.round(percentile));
          insightText = (
            <span>
              {currentValue.toFixed(3)} is within the typical range ({ordinalPercentile} percentile) of <i>M</i> values over the last 10 years
            </span>
          );
        }

      setInsights(insightText);
    } catch (error) {
      console.error('Error fetching M value insights:', error);
      setInsights(null);
    } finally {
      setIsLoadingInsights(false);
    }
  }, [getBasParameterTracking, updatingPStar, selectedStockId]);

  // Fetch insights when the value changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateInsights(naturalMortalityValue);
    }, 500); // Debounce the API call

    return () => clearTimeout(timeoutId);
  }, [naturalMortalityValue, calculateInsights]);

  return (
    <SliderBase
      title={
        <span>
          Natural Mortality (<i>M<sub>new</sub></i>)
        </span>
      }
      value={naturalMortalityValue}
      dbValue={dbNaturalMortalityValue}
      min={0.01}
      max={1.0}
      step={0.01}
      onChange={handleNaturalMortalityChange}
      disabled={updatingPStar}
      valueFormat={v => v.toFixed(3)}
      insights={isLoadingInsights ? 'Loading insights...' : insights}
      onInsightsClick={onInsightsClick}
    />
  );
};


// Add these to SliderControls.jsx:

export const SAMSDASSlider = ({ 
  samsInputDAS, 
  dbSamsInputDAS, 
  handleSamsDASChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Days at Sea (DAS) per Vessel"
      value={samsInputDAS}
      dbValue={dbSamsInputDAS}
      min={0}
      max={50}
      step={1}
      onChange={handleSamsDASChange}
      disabled={updatingParams}
      valueFormat={v => `${v.toFixed(0)} days`}
    />
  );
};

export const SAMSFleetSizeSlider = ({ 
  samsFleetSize, 
  dbSamsFleetSize, 
  handleFleetSizeChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Fleet Size"
      value={samsFleetSize}
      dbValue={dbSamsFleetSize}
      min={100}
      max={500}
      step={10}
      onChange={handleFleetSizeChange}
      disabled={updatingParams}
      valueFormat={v => `${v.toFixed(0)} vessels`}
    />
  );
};

export const SAMSProjectionYearsSlider = ({ 
  samsNYears, 
  dbSamsNYears, 
  handleProjectionYearsChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Projection Years"
      value={samsNYears}
      dbValue={dbSamsNYears}
      min={1}
      max={10}
      step={1}
      onChange={handleProjectionYearsChange}
      disabled={updatingParams}
      valueFormat={v => `${v.toFixed(0)} years`}
    />
  );
};

// WHAM Projection Slider - Add this to the end of SliderControls.jsx

export const WhamProjectionSlider = ({ 
  oflValues = [],           // Array of OFL catch values from baseline
  projectionYears = [],     // Array of year labels [2025, 2026, 2027, 2028]
  onChange,                 // Callback when slider changes
  disabled = false 
}) => {
  // State management
  const [mortalityMode, setMortalityMode] = useState(0); // 0=OFL, 1=ABC, 2=Util
  const [abcControlRule, setAbcControlRule] = useState(75); // 50-100%
  const [utilizationRate, setUtilizationRate] = useState(85); // 50-100%

  // Calculate current projected catch values based on mode
  const calculateProjectedCatch = useCallback(() => {
    if (mortalityMode === 0) {
      // OFL mode - use baseline values directly
      return oflValues;
    } else if (mortalityMode === 1) {
      // ABC mode - apply control rule to OFL
      return oflValues.map(ofl => ofl * (abcControlRule / 100));
    } else {
      // Utilization mode - apply both control rule and utilization
      const abcValues = oflValues.map(ofl => ofl * (abcControlRule / 100));
      return abcValues.map(abc => abc * (utilizationRate / 100));
    }
  }, [mortalityMode, oflValues, abcControlRule, utilizationRate]);

  // Build WHAM API parameters
  const buildWhamApiParams = useCallback(() => {
  const projectedCatch = calculateProjectedCatch();
  const maxYears = 6; // Cap at 6 years maximum
  const actualYears = Math.min(oflValues.length, maxYears);
  
  if (mortalityMode === 0) {
    // OFL mode: first year specified, rest use FXSPR
    return {
      bridge_catch: oflValues[0] || 0,
      n_yrs: actualYears,
      proj_f_opt: oflValues.slice(0, actualYears).map((_, i) => i === 0 ? 5 : 3), // ← slice here
      proj_catch: oflValues.slice(0, actualYears).map((val, i) => i === 0 ? val : 0) // 0 placeholder for FXSPR years; WHAM uses proj_f_opt to determine catch
    };
  } else {
    // ABC/Utilization mode: all years use specified catch
    return {
      bridge_catch: oflValues[0] || 0,
      n_yrs: actualYears,
      proj_f_opt: oflValues.slice(0, actualYears).map(() => 5), // ← slice here
      proj_catch: projectedCatch.slice(0, actualYears).map(c => Math.round(c)) // ← slice here
    };
  }
}, [mortalityMode, oflValues, calculateProjectedCatch]);

  // Notify parent of changes
  useEffect(() => {
    if (onChange && oflValues.length > 0) {
      const modeNames = ['OFL', 'ABC', 'Utilization'];
      const projectedCatch = calculateProjectedCatch();
      
      onChange({
        mode: modeNames[mortalityMode],
        mortalityMode: mortalityMode,
        projectedCatch: projectedCatch,
        abcControlRule: abcControlRule,
        utilizationRate: utilizationRate,
        whamApiParams: buildWhamApiParams()
      });
    }
  }, [mortalityMode, abcControlRule, utilizationRate, oflValues, onChange, calculateProjectedCatch, buildWhamApiParams]);

  const projectedCatch = calculateProjectedCatch();
  const modeNames = ['OFL', 'ABC', 'Utilization'];
  const currentMode = modeNames[mortalityMode];

  // Don't render if no OFL values
  if (oflValues.length === 0) {
    return (
      <div className='value-control'>
        <div>
          <h3><b>Projection Mortality Strategy</b></h3>
          <div className="slider-value" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Loading baseline projections...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wham-projection-container">
      {/* Main Mode Slider */}
      <div className='value-control'>
        <div>
          <h3><b>Projection Mortality Strategy</b></h3>
          <div className="slider-value">
            {currentMode}
            {mortalityMode === 1 && ` (${abcControlRule}% of OFL)`}
            {mortalityMode === 2 && ` (${utilizationRate}% of ABC)`}
          </div>
        </div>
        
        <div className="slider-container">
          <div className="slider-minmax">OFL</div>
          <input
            type="range"
            min={0}
            max={2}
            step={1}
            value={mortalityMode}
            onChange={(e) => setMortalityMode(parseInt(e.target.value))}
            disabled={disabled}
            className="slider wham-mode-slider"
          />
          <div className="slider-minmax">Utilization</div>
        </div>

        {/* Mode markers */}
        <div className="wham-mode-markers">
          <span className={mortalityMode === 0 ? 'active' : ''}>OFL</span>
          <span className={mortalityMode === 1 ? 'active' : ''}>ABC</span>
          <span className={mortalityMode === 2 ? 'active' : ''}>Util</span>
        </div>
      </div>

      {/* ABC Control Rule Sub-Slider */}
      {mortalityMode === 1 && (
        <div className='value-control wham-sub-slider'>
          <div>
            <h4><b>ABC Control Rule</b></h4>
            <div className="slider-value">{abcControlRule}% of OFL</div>
          </div>
          
          <div className="slider-container">
            <div className="slider-minmax">50%</div>
            <input
              type="range"
              min={50}
              max={100}
              step={1}
              value={abcControlRule}
              onChange={(e) => setAbcControlRule(parseFloat(e.target.value))}
              disabled={disabled}
              className="slider wham-abc-slider"
            />
            <div className="slider-minmax">100%</div>
          </div>
        </div>
      )}

      {/* Utilization Rate Sub-Slider */}
      {mortalityMode === 2 && (
        <>
          {/* Show ABC control rule (read-only) */}
          <div className='value-control wham-sub-slider wham-info-display'>
            <div>
              <h4><b>ABC Control Rule (Set)</b></h4>
              <div className="slider-value">{abcControlRule}% of OFL</div>
            </div>
          </div>

          {/* Utilization slider */}
          <div className='value-control wham-sub-slider'>
            <div>
              <h4><b>Utilization Rate</b></h4>
              <div className="slider-value">{utilizationRate}% of ABC</div>
            </div>
            
            <div className="slider-container">
              <div className="slider-minmax">50%</div>
              <input
                type="range"
                min={50}
                max={100}
                step={1}
                value={utilizationRate}
                onChange={(e) => setUtilizationRate(parseFloat(e.target.value))}
                disabled={disabled}
                className="slider wham-util-slider"
              />
              <div className="slider-minmax">100%</div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};


export const SAMSLpueMethodSlider = ({ 
  samsLpueMethod, 
  dbSamsLpueMethod, 
  handleLpueMethodChange, 
  updatingParams,
  lpueOptions = []
}) => {
  // Get the display label for current value
  const getCurrentLabel = () => {
    const option = lpueOptions.find(opt => opt.value === samsLpueMethod);
    return option?.label || samsLpueMethod || 'Loading...';
  };

  return (
    <div className='value-control'>
      <div>
        <h3><b>LPUE Calculation Method</b></h3>
        <div className="slider-value">{getCurrentLabel()}</div>
      </div>
      <div className="slider-container">
        <select
          value={samsLpueMethod}
          onChange={(e) => {
            console.log('🔄 LPUE Dropdown changed!');
            console.log('  Old value:', samsLpueMethod);
            console.log('  New value:', e.target.value);
            handleLpueMethodChange(e);
          }}
          disabled={updatingParams || lpueOptions.length === 0}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.1)',
            color: 'inherit',
            fontSize: '14px'
          }}
        >
          {lpueOptions.length === 0 ? (
            <option value="">Loading options...</option>
          ) : (
            lpueOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          )}
        </select>
      </div>
      
      {dbSamsLpueMethod !== undefined && samsLpueMethod !== dbSamsLpueMethod && (
        <div className="db-value-indicator">
          Baseline value: {dbSamsLpueMethod}
        </div>
      )}
      
      {samsLpueMethod !== dbSamsLpueMethod && (
        <div className="value-changed-indicator"></div>
      )}
    </div>
  );
};

// Scallop Natural Mortality Slider - Georges Bank
export const SAMSNaturalMortalityGBSlider = ({ 
  samsNaturalMortalityGB, 
  dbSamsNaturalMortalityGB, 
  handleNaturalMortalityGBChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title={
        <span>
          Natural Mortality - Georges Bank (<i>M<sub>GB</sub></i>)
        </span>
      }
      value={samsNaturalMortalityGB}
      dbValue={dbSamsNaturalMortalityGB}
      min={0.05}
      max={1.0}
      step={0.01}
      onChange={handleNaturalMortalityGBChange}
      disabled={updatingParams}
      valueFormat={v => `${v.toFixed(3)} yr⁻¹`}
    />
  );
};

// Scallop Natural Mortality Slider - Mid-Atlantic
export const SAMSNaturalMortalityMASlider = ({ 
  samsNaturalMortalityMA, 
  dbSamsNaturalMortalityMA, 
  handleNaturalMortalityMAChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title={
        <span>
          Natural Mortality - Mid-Atlantic (<i>M<sub>MA</sub></i>)
        </span>
      }
      value={samsNaturalMortalityMA}
      dbValue={dbSamsNaturalMortalityMA}
      min={0.05}
      max={1.0}
      step={0.01}
      onChange={handleNaturalMortalityMAChange}
      disabled={updatingParams}
      valueFormat={v => `${v.toFixed(3)} yr⁻¹`}
    />
  );
};

// Lobster Bait Supply Sliders - Add these to the end of SliderControls.jsx

/** First calendar year for projection labels (2026, 2027, 2028 by default). */
const DEFAULT_PROJECTION_START_YEAR = 2026;

export const LobsterHerringSupplySlider = ({ 
  herringSupply = [],           // Array of herring supply values by year [6741, 10885, 15435]
  dbHerringSupply = [],         // Database values
  projectionYears = 3,          // Number of years in projection
  projectionStartYear = DEFAULT_PROJECTION_START_YEAR, // First year label (e.g. 2026)
  onChange,                     // Callback when any year changes
  disabled = false 
}) => {
  const [yearValues, setYearValues] = useState(herringSupply);

  // Initialize year values when props change
  useEffect(() => {
    if (herringSupply.length > 0) {
      setYearValues(herringSupply);
    }
  }, [herringSupply]);

  // Handle individual year change
  const handleYearChange = (yearIndex, newValue) => {
    const updatedValues = [...yearValues];
    updatedValues[yearIndex] = parseFloat(newValue);
    setYearValues(updatedValues);
    
    if (onChange) {
      onChange(updatedValues);
    }
  };

  // Ensure we have values for all years
  const ensureYearValues = () => {
    const values = [...yearValues];
    while (values.length < projectionYears) {
      values.push(values[values.length - 1] || 10000); // Default to 10,000 MT or last value
    }
    return values.slice(0, projectionYears);
  };

  const currentYearValues = ensureYearValues();

  return (
    <div className="multi-year-slider-container">
      <div className='value-control'>
        <div>
          <h3><b>Herring Supply (MT)</b></h3>
          <div className="slider-value">
            {projectionYears} year{projectionYears > 1 ? 's' : ''} configured
          </div>
        </div>
      </div>

      {currentYearValues.map((value, index) => (
        <div key={`herring-year-${index}`} className='value-control'>
          <div>
            <h3><b>Year {projectionStartYear + index} Supply</b></h3>
            <div className="slider-value">{value.toLocaleString()} MT</div>
          </div>
          
          <div className="slider-container">
            <div className="slider-minmax">1,000</div>
            <input
              type="range"
              min={1000}
              max={30000}
              step={100}
              value={value}
              onChange={(e) => handleYearChange(index, e.target.value)}
              disabled={disabled}
              className="slider"
            />
            <div className="slider-minmax">30,000</div>
          </div>
          {dbHerringSupply[index] !== undefined && value !== dbHerringSupply[index] && (
            <div className="db-value-indicator">
              Database value: {dbHerringSupply[index].toLocaleString()} MT
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const LobsterMenhadenSupplySlider = ({ 
  menhadenSupply = [],          // Array of menhaden supply values by year [60000, 48000, 48000]
  dbMenhadenSupply = [],        // Database values
  projectionYears = 3,          // Number of years in projection
  projectionStartYear = DEFAULT_PROJECTION_START_YEAR, // First year label (e.g. 2026)
  onChange,                     // Callback when any year changes
  disabled = false 
}) => {
  const [yearValues, setYearValues] = useState(menhadenSupply);

  // Initialize year values when props change
  useEffect(() => {
    if (menhadenSupply.length > 0) {
      setYearValues(menhadenSupply);
    }
  }, [menhadenSupply]);

  // Handle individual year change
  const handleYearChange = (yearIndex, newValue) => {
    const updatedValues = [...yearValues];
    updatedValues[yearIndex] = parseFloat(newValue);
    setYearValues(updatedValues);
    
    if (onChange) {
      onChange(updatedValues);
    }
  };

  // Ensure we have values for all years
  const ensureYearValues = () => {
    const values = [...yearValues];
    while (values.length < projectionYears) {
      values.push(values[values.length - 1] || 50000); // Default to 50,000 MT or last value
    }
    return values.slice(0, projectionYears);
  };

  const currentYearValues = ensureYearValues();

  return (
    <div className="multi-year-slider-container">
      <div className='value-control'>
        <div>
          <h3><b>Menhaden Supply (MT)</b></h3>
          <div className="slider-value">
            {projectionYears} year{projectionYears > 1 ? 's' : ''} configured
          </div>
        </div>
      </div>

      {currentYearValues.map((value, index) => (
        <div key={`menhaden-year-${index}`} className='value-control'>
          <div>
            <h3><b>Year {projectionStartYear + index} Supply</b></h3>
            <div className="slider-value">{value.toLocaleString()} MT</div>
          </div>
          
          <div className="slider-container">
          <div className="slider-minmax">
            {dbMenhadenSupply[index]
              ? (dbMenhadenSupply[index] * 0.9).toLocaleString(undefined, {maximumFractionDigits: 0})
              : '20,000'}
          </div>
          <input
            type="range"
            min={dbMenhadenSupply[index] ? dbMenhadenSupply[index] * 0.9 : 20000}
            max={dbMenhadenSupply[index] ? dbMenhadenSupply[index] * 1.1 : 100000}
            step={1000}
            value={value}
            onChange={(e) => handleYearChange(index, e.target.value)}
            disabled={disabled}
            className="slider"
          />
          <div className="slider-minmax">
            {dbMenhadenSupply[index]
              ? (dbMenhadenSupply[index] * 1.1).toLocaleString(undefined, {maximumFractionDigits: 0})
              : '100,000'}
          </div>
        </div>

          {dbMenhadenSupply[index] !== undefined && value !== dbMenhadenSupply[index] && (
            <div className="db-value-indicator">
              Database value: {dbMenhadenSupply[index].toLocaleString()} MT
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const LobsterImportUnitValueSlider = ({ 
  importUnitValue, 
  dbImportUnitValue, 
  handleImportUnitValueChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Bait Import Unit Value ($/lb)"
      value={importUnitValue}
      dbValue={dbImportUnitValue}
      min={0.05}
      max={0.50}
      step={0.01}
      onChange={handleImportUnitValueChange}
      disabled={updatingParams}
      valueFormat={v => `$${v.toFixed(2)}/lb`}
    />
  );
};



// Lobster Operating Parameters Sliders

export const LobsterFuelPriceSlider = ({ 
  fuelPrice, 
  dbFuelPrice, 
  handleFuelPriceChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Fuel Price ($/gal)"
      value={fuelPrice}
      dbValue={dbFuelPrice}
      min={1.50}
      max={8.00}
      step={0.10}
      onChange={handleFuelPriceChange}
      disabled={updatingParams}
      valueFormat={v => `$${v.toFixed(2)}/gal`}
    />
  );
};

export const LobsterTrapPriceSlider = ({ 
  trapPrice, 
  dbTrapPrice, 
  handleTrapPriceChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Trap Price ($)"
      value={trapPrice}
      dbValue={dbTrapPrice}
      min={50}
      max={600}
      step={5}
      onChange={handleTrapPriceChange}
      disabled={updatingParams}
      valueFormat={v => `$${v.toFixed(0)}`}
    />
  );
};

export const LobsterRopePriceSlider = ({ 
  ropePrice, 
  dbRopePrice, 
  handleRopePriceChange, 
  updatingParams 
}) => {
  return (
    <SliderBase
      title="Rope Price ($/ft)"
      value={ropePrice}
      dbValue={dbRopePrice}
      min={0.05}
      max={5.00}
      step={0.05}
      onChange={handleRopePriceChange}
      disabled={updatingParams}
      valueFormat={v => `$${v.toFixed(2)}/ft`}
    />
  );
};

export const LobsterRopelessGearToggle = ({ 
  ropelessGear, 
  dbRopelessGear, 
  toggleRopelessGear, 
  updatingParams 
}) => {
  return (
    <div className='value-control'>
      <div>
        <h3><b>Ropeless Gear Technology</b></h3>
        <div className="slider-value">{ropelessGear ? 'Enabled' : 'Disabled'}</div>
      </div>
      
      <div className="toggle-container" style={{ marginTop: '12px' }}>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={ropelessGear}
            onChange={toggleRopelessGear}
            disabled={updatingParams}
          />
          <span className="toggle-slider"></span>
        </label>
        <span style={{ marginLeft: '12px', fontSize: '14px' }}>
          {ropelessGear ? 'Using ropeless technology' : 'Using traditional rope'}
        </span>
      </div>
      
      {dbRopelessGear !== undefined && ropelessGear !== dbRopelessGear && (
        <div className="db-value-indicator">
          Baseline value: {dbRopelessGear ? 'Enabled' : 'Disabled'}
        </div>
      )}
      
      {ropelessGear !== dbRopelessGear && (
        <div className="value-changed-indicator"></div>
      )}
    </div>
  );
};